import React from "react";
import LegalLayout from "../components/LegalLayout";

const TermsPage: React.FC = () => (
  <LegalLayout
    title="Terms of Service"
    path="/terms"
    description="The terms that govern your use of the PABS platform, website, and applications."
    intro="The terms that govern your use of the PABS platform, website, and applications."
  >
        <p>
          These Terms govern your access to and use of PABS (the “Service”), including our website and
          applications. By using the Service, you agree to these Terms.
        </p>

        <h2>Service overview</h2>
        <p>
          PABS provides an online delivery platform that lets you request on-demand deliveries
          (food, groceries, pharmacy items, and other essential products).
        </p>
        <p>
          The Service connects Customers with independent Delivery Partners. PABS itself does not
          directly perform deliveries; each service is fulfilled by an independent Delivery Partner
          using the platform.
        </p>

        <h2>Accounts</h2>
        <ul>
          <li>You’re responsible for activities that happen under your account.</li>
          <li>You must provide accurate information and keep it up to date.</li>
          <li>Keep your credentials secure and notify us of suspected unauthorized access.</li>
        </ul>

        <h2>Orders, deliveries, and service requests</h2>
        <ul>
          <li>
            When you submit an order or delivery request, you are offering to receive a service from an
            independent Delivery Partner. A binding service agreement is formed between you and the
            Delivery Partner only when a Delivery Partner accepts the request.
          </li>
          <li>Provide accurate pickup (if applicable), drop-off, and order details when creating a request.</li>
          <li>Availability depends on Delivery Partner capacity, service coverage, and platform rules.</li>
          <li>Status updates (e.g., accepted, en route, picked up, completed) are shown in the app.</li>
        </ul>

        <h2>Payments and fees</h2>
        <p>
          Delivery fees may vary based on service type, distance, time, and applicable charges.
          Estimates may use a base fee plus per‑kilometer and/or time components depending on
          the service.
        </p>
        <p>
          Where items are purchased (for example, groceries or food), the item cost is separate from
          the delivery fee and may be handled according to the payment preference shown in the app.
          You’ll see an estimate before confirming a request, and final amounts may adjust based on
          actual distance, time, or changes to the request.
        </p>
        <p>
          By using the Service, you authorize PABS to process payments on behalf of Delivery Partners and to
          collect any applicable platform fees. Payment methods available to you are shown during
          checkout and may vary by service or location.
        </p>

        <h2>Cancellations and no-shows</h2>
        <p>
          Cancellation rules may differ by service type and by the stage of the request. Once a
          Delivery Partner has accepted a request, cancellations may be limited and, where applicable,
          cancellation or no-show fees may apply. Any applicable terms are shown during the ordering
          flow or in the app.
        </p>

        <h2>Acceptable use</h2>
        <ul>
          <li>Don’t misuse the Service or interfere with its operation.</li>
          <li>Don’t attempt to access accounts, data, or systems you aren’t authorized to access.</li>
          <li>Don’t upload or request content that violates applicable law.</li>
        </ul>

        <h2>User content</h2>
        <p>
          You retain ownership of content you submit to the Service (such as notes or delivery details).
          You grant PABS a limited license to host, store, and display your content solely to provide the
          Service.
        </p>

        <h2>Third-party services</h2>
        <p>
          Some features rely on third-party services (for example: payments, maps, messaging, and security
          tooling). See our <a href="/subprocessors">Subprocessors</a> page for common vendors.
        </p>

        <h2>Termination</h2>
        <p>
          You may stop using the Service at any time. We may suspend or terminate access if you violate
          these Terms or if needed to protect the Service.
        </p>

        <h2>Liability</h2>
        <p>
          To the extent permitted by law, Delivery Partners and Customers are responsible for their own
          acts and omissions. PABS is not liable for delays, losses, or damages caused by Delivery Partners or
          Customers in connection with services arranged through the platform.
        </p>

        <h2>Disclaimers</h2>
        <p>
          The Service is provided “as is” and “as available”. To the maximum extent permitted by law,
          PABS disclaims warranties of merchantability, fitness for a particular purpose, and
          non-infringement.
        </p>

        <h2>Privacy</h2>
        <p>
          Our <a href="/privacy">Privacy Statement</a> explains how we handle personal information.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about these Terms: <a href="mailto:support@pabs.online">support@pabs.online</a>
        </p>

        <p className="legal-note">
          This page is provided for general informational purposes and may be updated from time to time.
        </p>
  </LegalLayout>
);

export default TermsPage;
